import { Link } from 'react-router';
import './PrivacyPage.css';

function PrivacyPage() {
    return (
        <div className="privacy-page">
            <div className="privacy-inner">

                <div className="privacy-eyebrow">Legal</div>
                <h1 className="privacy-title">Privacy Policy</h1>
                <p className="privacy-meta">
                    <strong>CertDrill</strong> · Effective date: February 26, 2026 · Last updated: February 26, 2026
                </p>

                <section className="privacy-section">
                    <h2 className="privacy-h2">Who we are</h2>
                    <p>CertDrill is an AI-powered certification exam study platform operated by Joshua Kindy as an individual. If you have any questions about this policy, contact us at <a href="mailto:joshkindy@gmail.com">joshkindy@gmail.com</a>.</p>
                </section>

                <section className="privacy-section">
                    <h2 className="privacy-h2">What data we collect</h2>

                    <h3 className="privacy-h3">Account information</h3>
                    <p>When you sign in, authentication is handled by Microsoft Azure Active Directory B2C. We receive your name and email address from Microsoft and store a unique identifier linked to your account. We do not store your password — Microsoft manages credentials entirely.</p>

                    <h3 className="privacy-h3">Study activity</h3>
                    <p>We store the answers you submit, confidence ratings you record, questions you have answered, study goals you set, and your answer history. This data is used solely to personalise your study experience.</p>

                    <h3 className="privacy-h3">Comments and feedback</h3>
                    <p>If you post a comment on a question or submit feedback through the feedback form, the content of that submission is stored in our database.</p>

                    <h3 className="privacy-h3">Usage data</h3>
                    <p>We do not run analytics trackers or advertising pixels. We do not collect IP addresses, device fingerprints, or browsing behaviour beyond what is described above.</p>
                </section>

                <section className="privacy-section">
                    <h2 className="privacy-h2">How we use your data</h2>
                    <ul className="privacy-list">
                        <li>To deliver and personalise the study experience</li>
                        <li>To track your progress and confidence over time</li>
                        <li>To generate AI-powered questions and grade your answers</li>
                        <li>To display relevant YouTube videos for further learning</li>
                        <li>To moderate comments for inappropriate content before they are posted</li>
                    </ul>
                    <p>We do not use your data for advertising, profiling, or any purpose beyond operating the platform.</p>
                </section>

                <section className="privacy-section">
                    <h2 className="privacy-h2">Third parties we share data with</h2>
                    <p>We do not sell your data. We share limited data with the following service providers solely to operate the platform:</p>

                    <div className="privacy-table-wrap">
                        <table className="privacy-table">
                            <thead>
                                <tr>
                                    <th>Provider</th>
                                    <th>Purpose</th>
                                    <th>Data shared</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Microsoft Azure</strong></td>
                                    <td>Authentication, database, and hosting</td>
                                    <td>Account identity, all app data at rest</td>
                                </tr>
                                <tr>
                                    <td><strong>OpenAI</strong></td>
                                    <td>Question generation and answer grading</td>
                                    <td>Question text and your submitted answers</td>
                                </tr>
                                <tr>
                                    <td><strong>Google / YouTube</strong></td>
                                    <td>Relevant video suggestions</td>
                                    <td>No personal data — only topic keywords</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <p>Each provider has their own privacy policy. OpenAI's policy is at <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">openai.com/policies/privacy-policy</a>. Microsoft's is at <a href="https://privacy.microsoft.com" target="_blank" rel="noopener noreferrer">privacy.microsoft.com</a>.</p>
                </section>

                <section className="privacy-section">
                    <h2 className="privacy-h2">Data retention</h2>
                    <p>Your study data is retained for as long as your account is active. If you would like your data deleted, email <a href="mailto:joshkindy@gmail.com">joshkindy@gmail.com</a> and we will remove it within 30 days.</p>
                </section>

                <section className="privacy-section">
                    <h2 className="privacy-h2">Your rights</h2>
                    <p>Regardless of where you are located, you can:</p>
                    <ul className="privacy-list">
                        <li><strong>Access</strong> the data we hold about you</li>
                        <li><strong>Correct</strong> inaccurate data</li>
                        <li><strong>Request deletion</strong> of your data</li>
                        <li><strong>Request a copy</strong> of your data in a portable format</li>
                    </ul>
                    <p>To exercise any of these rights, email <a href="mailto:joshkindy@gmail.com">joshkindy@gmail.com</a>.</p>
                    <p><strong>EU / UK users (GDPR):</strong> Our legal basis for processing your data is contractual necessity — we need it to provide the service you signed up for. You also have the right to lodge a complaint with your local data protection authority.</p>
                    <p><strong>California users (CCPA):</strong> We do not sell personal information. You have the right to know what data we collect and to request its deletion.</p>
                </section>

                <section className="privacy-section">
                    <h2 className="privacy-h2">Security</h2>
                    <p>Your data is stored on Microsoft Azure infrastructure and protected by industry-standard security practices including encryption at rest and in transit. Authentication tokens are handled entirely by Microsoft and are never stored on our servers.</p>
                </section>

                <section className="privacy-section">
                    <h2 className="privacy-h2">Children</h2>
                    <p>CertDrill is an educational platform open to users of all ages. If you are under 13, please ensure a parent or guardian has reviewed this privacy policy and consents to your use of the platform. We do not knowingly collect any data beyond what is described in this policy, and no data is used for advertising or shared with third parties for commercial purposes.</p>
                    <p>If you are a parent or guardian with concerns about your child's account, contact us at <a href="mailto:joshkindy@gmail.com">joshkindy@gmail.com</a> and we will address them promptly.</p>
                </section>

                <section className="privacy-section">
                    <h2 className="privacy-h2">Changes to this policy</h2>
                    <p>If we make material changes, we will update the "Last updated" date at the top of this page. Continued use of the platform after changes constitutes acceptance of the updated policy.</p>
                </section>

                <section className="privacy-section">
                    <h2 className="privacy-h2">Contact</h2>
                    <p><strong>Joshua Kindy</strong><br /><a href="mailto:joshkindy@gmail.com">joshkindy@gmail.com</a></p>
                </section>

                <div className="privacy-back">
                    <Link to="/" className="privacy-back-link">← Back to home</Link>
                </div>

            </div>
        </div>
    );
}

export default PrivacyPage;
